<template>
    <!-- Fixed width: the center column absorbs every layout change. The id is
         how RightPanel measures the space left for its effective width. -->
    <div class="pl-2 pr-2 h-screen flex flex-col shrink-0" id="left_sidebar">
        <div class="pb-2 space-y-2 w-64 flex-1 min-h-0 overflow-y-auto hide-scrollbar">
            <form-label name="Design" />
            <Accordion :is_open="true" class="pr-2 pl-2 space-y-1 bg-opacity-30 bg-slate-400" title="Components">
                <div class="grid grid-cols-5 gap-1">
                    <template v-for="(name, obj) in objectTypelookup" :key="obj">
                        <div v-if="lookupIcon[obj]">
                            <form-button :disabled="disableComponent(selectedNode, obj, activePage)" color="theme"
                                :icon="lookupIcon[obj].icon"
                                :docicon="appSettings.ui.show_doc_icon ? lookupIcon[obj].docicon : null"
                                :title="lookupIcon[obj].title"
                                @click="$emit('add-object', obj)">{{ obj }}</form-button>
                        </div>
                    </template>
                </div>
            </Accordion>
            <!-- CUSTOM COMPONENTS -->
            <Accordion :is_open="true" class="pr-2 pl-2 space-y-1 bg-opacity-30 bg-slate-400" title="Custom Components">
                <div class="space-y-1">
                    <div class="grid grid-cols-5 gap-1">
                        <form-button
                            :disabled="!selectedNode || selectedNode.obj === 'page'"
                            color="success"
                            icon="media/icons/newCustomComponent.png"
                            title="Save selected object as a custom component"
                            @click="$emit('open-save-custom-component')">
                            create custom component
                        </form-button>
                        <form-button
                            color="theme"
                            icon="media/icons/customComponent.png"
                            title="Manage custom components (edit, delete, import, export)"
                            @click="$emit('open-custom-manager')">
                            manage custom components
                        </form-button>
                    </div>
                    <div v-if="customComponents.length === 0" class="text-gray-400 text-xs text-center py-1">
                        No custom components yet
                    </div>
                    <div v-else class="grid grid-cols-5 gap-1">
                        <div v-for="comp in customComponents" :key="comp.id">
                            <form-button
                                color="theme"
                                :icon="comp.icon || undefined"
                                :docicon="comp.icon || undefined"
                                :title="comp.name + (comp.description ? '\n' + comp.description : '')"
                                @click="$emit('place-custom-component', comp)">
                                {{ comp.name.slice(0, 5) }}
                            </form-button>
                        </div>
                    </div>
                </div>
            </Accordion>
            <!-- EVENTS & ACTIONS (open in Logic mode only, so collapsed at startup) -->
            <Accordion :is_open="canvasMode === 'logic'" class="pr-2 pl-2 space-y-1 bg-opacity-30 bg-slate-400" title="Events & Actions">
                <LogicPalette @add="$emit('add-logic-node', $event)" />
            </Accordion>
            <!-- HOME ASSISTANT FLOWS (open in HA mode only, so collapsed at startup) -->
            <Accordion :is_open="canvasMode === 'hass'" class="pr-2 pl-2 space-y-1 bg-opacity-30 bg-slate-400" title="Home Assistant">
                <HassPalette @add="$emit('add-hass-node', $event)" />
            </Accordion>
            <!-- Settings -->
            <Accordion :is_open="true" class="pr-2 pl-2 space-y-1 bg-opacity-30 bg-slate-400" title="Settings">
                <div class="space-y-1">
                    <div class="grid grid-cols-5 gap-1">
                        <form-button color="theme" icon="media/icons/plate.png"
                            title="Configure Plates"
                            @click="$emit('open-plates')">Plate configuration</form-button>
                        <form-button color="theme"
                            title="Edit plate type templates (board definitions and screen sizes)"
                            icon="media/icons/plateSettings.png"
                            @click="$emit('open-plate-types')">Plate Types</form-button>
                        <div/>
                        <div/>
                        <form-button color="theme"
                            title="Open Settings for UI and connections MQTT and Homeassistant"
                            icon="media/icons/settings.png"
                            @click="$emit('open-settings')">Settings
                        </form-button>
                    </div>
                </div>
            </Accordion>
            <!-- TOOLS -->
            <Accordion :is_open="true" class="pr-2 pl-2 space-y-1 bg-opacity-30 bg-slate-400" title="Tools">
                <div class="grid grid-cols-5 gap-1">
                    <form-button :disabled="disableDelete(selectedNode)" color="danger"
                        title="Select the object to be deleted first." icon="media/icons/trash.png"
                        @click="$emit('delete')">delete object</form-button>
                    <form-button :disabled="disableCopy(selectedNode)" color="theme"
                        title="Copy the selected object with all its children." icon="media/icons/copy.png"
                        @click="$emit('copy')">copy object</form-button>
                    <form-button :disabled="disableScreeshot(selectedNode)" color="theme"
                        title="Create a screenshot of the selected object." icon="media/icons/screenshot.png"
                        @click="$emit('screenshot')">screenshot</form-button>
                    <form-button color="theme" icon="media/icons/docs.png"
                        title="Open documentation viewer"
                        @click="$emit('open-docs')">Docs</form-button>
                    <form-button color="danger" @dblclick="$emit('new-project')"
                        icon="media/icons/newProject.png"
                        title="New Project. Your current design will be lost! Double click!">New
                        Project</form-button>
                    <form-button :disabled="!canUndo" color="theme"
                        icon="media/icons/undoLeft.png"
                        title="Undo (Ctrl+Z)" @click="$emit('undo')"/>
                    <form-button :disabled="!canRedo" color="theme"
                        icon="media/icons/redoRight.png"
                        title="Redo (Ctrl+Y)" @click="$emit('redo')"/>
                    <form-button :disabled="!selectedNode || selectedNode.obj === 'page' || selectedNode.obj === 'template_page'" color="theme"
                        title="Send Backward — move behind the next object"
                        icon="media/icons/layerDown.png"
                        @click="selectedNode.layerMoveDown()"/>
                    <form-button :disabled="!selectedNode || selectedNode.obj === 'page' || selectedNode.obj === 'template_page'" color="theme"
                        title="Bring to Front — move on top of all objects"
                        @click="selectedNode.moveToTop()"
                        icon="media/icons/layerTop.png"/>
                    <form-button :disabled="!selectedNode || selectedNode.obj === 'page' || selectedNode.obj === 'template_page'" color="theme"
                        title="Bring Forward — move in front of the next object"
                        @click="selectedNode.moveUp()"
                        icon="media/icons/layerUp.png"/>
                    <hr class="col-span-5 border-t-2 border-slate-400" />
                    <div class="col-span-5 space-y-1">
                        <div class="grid grid-cols-5 gap-2">
                            <form-button color="success" icon="media/icons/open.png"
                                title="Open a design package (.zip). Pages, objects, assets and fonts are restored automatically."
                                @click="$refs.file.click()">open</form-button>
                            <form-button color="success" icon="media/icons/save.png"
                                title="Save the design as a design package (.zip)."
                                @click="$emit('save-design')">save</form-button>
                            <form-button color="theme" icon="media/icons/github.svg"
                                title="Push or pull this design to/from a GitHub repository."
                                @click="$emit('open-github-sync')">GitHub</form-button>
                            <form-button color="theme" icon="media/icons/import.png"
                                title="Import from Plate: a raw OpenHASP export or legacy Designer file pulled from a device. Converted into a design package on import."
                                @click="$refs.legacyFile.click()">import plate</form-button>
                            <form-button color="theme" icon="media/icons/export.png"
                                title="Export the design to openHASP format."
                                @click="$emit('open-export')">export plate</form-button>
                            <form-button color="theme" icon="media/icons/hass_export.png"
                                title="Export the design to openHASP format."
                                @click="$emit('open-hass-export')">export hass yaml</form-button>
                            <form-button color="" icon="media/icons/imageStore.png"
                                title="Open Image Store"
                                @click="$emit('open-image-store')">Image Store</form-button>
                            <form-button color="" icon="media/icons/scripts.png"
                                title="Visual script editor — build boot, online and idle scripts without writing code"
                                @click="$emit('open-scripts')">Scripts</form-button>
                            <form-button v-if="appSettings.tauri" color="theme" icon="media/icons/telnet.png"
                                :title="'Telnet to your plate. \nYour currently selected  ' +
                                    appSettings.plate.name +
                                    ' from your plates'
                                    " @click="$emit('open-telnet')">Telnet device</form-button>
                            <form-button v-if="appSettings.tauri" color="theme" icon="media/icons/mqttIcon.png"
                                :title="'Monitor plate using MQTT. \nYour currently selected ' +
                                    appSettings.plate.name +
                                    ' from your plates'
                                    " @click="$emit('open-mqtt')">Monitor device</form-button>
                            <form-button v-if="appSettings.tauri" color="theme" icon="media/icons/imageStore.png"
                                :title="'Sync resource files over HTTP. \nYour currently selected ' +
                                    appSettings.plate.name +
                                    ' from your plates'
                                    " @click="$emit('open-ftp')">Sync files to device</form-button>
                        </div>
                    </div>
                </div>
                <input class="hidden" @change="onFileChange" ref="file" type="file" accept=".zip" />
                <input class="hidden" @change="onLegacyFileChange" ref="legacyFile" type="file" accept=".jsonl" />
            </Accordion>
        </div>
    </div>
</template>

<script>
import { objectTypelookup, lookupIcon } from '../utils/lookupTables';
import { disableComponent, disableCopy, disableDelete, disableScreeshot } from '../utils/buttonLogic';
import Accordion from './Accordion.vue';
import LogicPalette from './logic/LogicPalette.vue';
import HassPalette from './hass/HassPalette.vue';
import { useCanvasModeStore } from '../stores/canvasModeStore.js';

export default {
    name: 'LeftSidebar',
    components: { Accordion, LogicPalette, HassPalette },
    props: {
        appSettings: { type: Object, required: true },
        selectedNode: { type: Object, default: undefined },
        activePage: { type: Object, default: undefined },
        customComponents: { type: Array, default: () => [] },
        canUndo: { type: Boolean, default: false },
        canRedo: { type: Boolean, default: false },
    },
    emits: [
        'add-object', 'open-save-custom-component', 'open-custom-manager',
        'open-plates', 'open-plate-types', 'open-settings',
        'delete', 'copy', 'screenshot', 'open-docs', 'new-project',
        'undo', 'redo',
        'save-design', 'open-export', 'open-hass-export', 'open-image-store',
        'open-scripts', 'open-telnet', 'open-mqtt', 'open-ftp',
        'open-github-sync', 'place-custom-component', 'import-file', 'import-legacy-file',
        'add-logic-node', 'add-hass-node',
    ],
    data() {
        return {
            objectTypelookup,
            lookupIcon,
        };
    },
    computed: {
        canvasMode() { return useCanvasModeStore().mode; },
    },
    methods: {
        disableComponent,
        disableDelete,
        disableCopy,
        disableScreeshot,
        onFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.$emit('import-file', file);
                event.target.value = '';
            }
        },
        onLegacyFileChange(event) {
            const file = event.target.files[0];
            if (file) {
                this.$emit('import-legacy-file', file);
                event.target.value = '';
            }
        },
    },
};
</script>
